import React, { useEffect, useRef, useState } from 'react';
import YouTubeEmbed from '../YouTubeEmbed.jsx';
import { AudioList } from '../ReaderAudio.jsx';
import { freePlaybackUrl } from '../audioUrl.js';
import { lessonYouTubeId, mediaVisible } from './model.js';

function ImagePreview({ row }) {
    const [broken, setBroken] = useState(false), [open, setOpen] = useState(false);
    const dialog = useRef(null);
    const src = freePlaybackUrl(row.imageUrl);
    useEffect(() => {
        if (!open || !dialog.current) return;
        const current = dialog.current;
        if (typeof current.showModal === 'function') current.showModal();
        else current.setAttribute('open', '');
        return () => { if (current.open && typeof current.close === 'function') current.close(); };
    }, [open]);
    if (broken) return <p role="status" className="text-sm text-book-muted">{row.fallback || 'This image could not load. ' + row.alt}</p>;
    return <>
        <button type="button" className="block max-w-full rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-book-accent" onClick={() => setOpen(true)} aria-label={'Enlarge ' + (row.title || row.alt)}>
            <img src={src} alt={row.alt} loading="lazy" decoding="async" onError={() => setBroken(true)} className="max-w-full max-h-96 w-auto rounded-xl" />
        </button>
        {open && <dialog ref={dialog} onCancel={() => setOpen(false)} onClose={() => setOpen(false)}
            aria-label={row.title || 'Enlarged lesson image'} className="max-w-[95vw] max-h-[95vh] bg-book-bg text-book-text border border-book-line rounded-xl p-4 backdrop:bg-black/70"
            onClick={event => { if (event.target === event.currentTarget) setOpen(false); }}>
            <div className="flex justify-end mb-3"><button type="button" autoFocus className="border border-book-line rounded-full px-3 py-2" onClick={() => setOpen(false)}>Close image</button></div>
            <img src={src} alt={row.alt} className="max-w-full max-h-[75vh] object-contain" />
            {row.caption && <p className="text-sm mt-3">{row.caption}</p>}
        </dialog>}
    </>;
}

function AudioAttachment({ row, availableAudios, packId }) {
    const [opened, setOpened] = useState(false);
    // The caller supplies only this reader's already-authorized audio items.
    // Never load a source or paid URL from the authored attachment itself.
    const audio = availableAudios.find(item => item?.id === row.audioItemId && item.type === 'audio');
    if (!audio) return <p role="status" className="text-sm text-book-muted">{row.fallback || 'This audio is unavailable. Please use the transcript or continue with the written activity.'}</p>;
    return opened
        ? <AudioList key={[packId, audio.id, audio._paid, audio.content?.storageUrl || audio.content?.storagePath].join('|')} heading={row.title || 'Lesson audio'} audios={[audio]} packId={packId} />
        : <button type="button" className="border border-book-accent/40 rounded-full px-4 py-2 text-book-accent" onClick={() => setOpened(true)}>Open audio player</button>;
}

function Attachment({ row, availableAudios, packId }) {
    return <figure data-lesson-media-id={row.id} data-media-type={row.type} data-media-role={row.role}
        className="border border-book-line bg-book-bg rounded-xl p-4 my-4 max-w-full overflow-hidden">
        {row.title && <div className="font-display font-semibold mb-3">{row.title}</div>}
        {row.type === 'image' && <ImagePreview key={row.imageUrl} row={row} />}
        {row.type === 'video' && <>
            <YouTubeEmbed key={row.videoUrl + (row.poster || '')} url={lessonYouTubeId(row.videoUrl)} title={row.title || 'Lesson video'} poster={row.poster} posterLoading="lazy" />
            <p className="text-sm text-book-muted mt-2">{row.fallback || 'If the video is unavailable, use the captions or transcript below, or continue with the written activity.'}</p>
        </>}
        {row.type === 'audio' && <AudioAttachment key={packId + ':' + row.audioItemId} row={row} availableAudios={availableAudios} packId={packId} />}
        {row.caption && <figcaption className="text-sm text-book-muted mt-3 whitespace-pre-wrap">{row.caption}</figcaption>}
        {row.sourceLabel && <p className="text-xs text-book-muted mt-2">Source: {row.sourceLabel}</p>}
        {['captions', 'transcript', 'pronunciation'].map(key => row[key] && <details key={key} className="mt-3 border-t border-book-line pt-2">
            <summary className="cursor-pointer text-sm font-semibold">{({ captions: 'Captions', transcript: 'Transcript', pronunciation: 'Pronunciation notes' })[key]}</summary>
            <p className="whitespace-pre-wrap text-sm mt-2">{row[key]}</p>
        </details>)}
    </figure>;
}

export default function LessonMedia({ content, packId, availableAudios = [], ...options }) {
    const visible = mediaVisible(content, options);
    if (!visible.length) return null;
    return <div className="lesson-media" data-lesson-media-placement={options.placement || 'before'}>
        {visible.map(row => <Attachment key={row.id} row={row} availableAudios={availableAudios} packId={packId} />)}
    </div>;
}
export { LessonMedia };
